import { BoxType, TextTypeEnum } from '@lite-workspace/dto-fotoclass-templates';
import type { CustomTemplateBox, CustomTextTemplateBox } from '@lite-workspace/dto-fotoclass-templates';

import { BookCoverData, BookData } from '../interfaces/book-data.interface';
import { DrawableData, DrawableTextData, isDrawableGroup, isDrawableText } from '../interfaces/drawable-data.interface';
import type { PersistedCollageV1, PersistedSpreadV1 } from '../normalizer/types';
import { carryStyleRunsForTextChange, withDrawableText } from '../text/drawable-text-change';
import { remapParagraphRunsForTextChange } from '../text/style-run-ranges';

/**
 * Cover text kinds a project can supply, in the order they are reported to the reader.
 * Any other textType on a cover is the author's own content and is never auto-filled or required.
 */
export const COVER_PROJECT_TEXT_TYPES: readonly string[] = [
  TextTypeEnum.SCHOOL,
  TextTypeEnum.CLASS,
  TextTypeEnum.CITY,
  TextTypeEnum.GRADUATION_YEAR,
];

const COVER_PROJECT_TEXT_TYPE_SET: ReadonlySet<string> = new Set<string>(COVER_PROJECT_TEXT_TYPES);

/**
 * `template` restates every project-owned slot; `refresh` only corrects one that already prints a word; `sync`
 * writes the value wherever there is one and, without it, blanks only the pack's sample.
 */
export type CoverProjectTextPass = 'template' | 'refresh' | 'sync';

function collectVisibleCoverTexts(drawables: readonly DrawableData[], out: DrawableTextData[]): void {
  for (const drawable of drawables) {
    // A hidden or non-rendered slot never reaches the reader, so it is neither fillable nor required. Tested
    // before the group recursion because a hidden group hides its whole subtree — the walk both renderers do.
    if (drawable.visible === false || drawable.skipRender === true) continue;

    if (isDrawableGroup(drawable)) {
      collectVisibleCoverTexts(drawable.drawableObjects, out);
      continue;
    }

    if (isDrawableText(drawable)) out.push(drawable);
  }
}

function visibleCoverTexts(cover: Pick<BookCoverData, 'collages'> | null | undefined): DrawableTextData[] {
  const found: DrawableTextData[] = [];
  for (const collage of cover?.collages ?? []) collectVisibleCoverTexts(collage.drawableObjects, found);
  return found;
}

/** Слот принадлежит проекту — один вопрос на обе персист-формы, расходится только адрес поля. */
function isProjectTextType(textType: string | undefined): boolean {
  return !!textType && COVER_PROJECT_TEXT_TYPE_SET.has(textType);
}

function isProjectTypedSlot(drawable: DrawableTextData): boolean {
  return isProjectTextType(drawable.textType);
}

/** Project-owned text slots physically present on the cover — the closed set both the autofill and the gate operate on. */
export function coverProjectTextFields(cover: Pick<BookCoverData, 'collages'> | null | undefined): readonly DrawableTextData[] {
  return visibleCoverTexts(cover).filter(isProjectTypedSlot);
}

export function isCoverTextFieldBlank(drawable: DrawableTextData): boolean {
  return (drawable.text?.text ?? '').trim() === '';
}

/**
 * What the cover currently PRINTS for each project-owned type. The fallback source for the rest of
 * the book (FKS-10460): an album whose `settings.textSettings` was never seeded — generated before
 * the seed existed, or restored from a model-only copy — still knows its class, because the cover
 * in front of the reader says it. Settings stay authoritative wherever they hold a value: they are
 * what the print render pushes back onto the cover, so following them keeps the two agreeing.
 *
 * Blank slots are omitted — an unfilled cover field states nothing, it does not state "empty".
 */
export function coverProjectTextValues(cover: Pick<BookCoverData, 'collages'> | null | undefined): Record<string, string> {
  const values: Record<string, string> = {};

  for (const drawable of coverProjectTextFields(cover)) {
    const text = (drawable.text?.text ?? '').trim();
    if (text) values[drawable.textType as string] = text;
  }

  return values;
}

/**
 * Project text settings, with the cover as the fallback: a type the settings hold no value for takes the word
 * the cover prints. Settings win wherever they hold one. The editor's load pass and the server's copy both feed
 * the vignette from here, so a copy the server wrote is one the editor finds already converged.
 * Returns `settings` itself when the cover adds nothing.
 */
export function withCoverProjectTextFallback<S extends object>(
  settings: S | null | undefined,
  cover: Pick<BookCoverData, 'collages'> | null | undefined,
): S {
  const base = (settings ?? {}) as Record<string, { text?: string | null } | undefined>;
  let merged: Record<string, unknown> | null = null;

  for (const [textType, text] of Object.entries(coverProjectTextValues(cover))) {
    if ((base[textType]?.text ?? '').trim() !== '') continue;
    merged ??= { ...base };
    merged[textType] = { ...base[textType], text };
  }

  return (merged ?? base) as S;
}

/**
 * THE definition of "a required cover field is missing" — shared verbatim by the editor,
 * the approval screen and the server gate. A second copy of this rule would drift.
 * Returns the distinct missing textTypes in COVER_PROJECT_TEXT_TYPES order.
 */
export function missingCoverTextTypes(book: Pick<BookData, 'cover'> | null | undefined): readonly string[] {
  const blank = new Set<string>();

  for (const drawable of coverProjectTextFields(book?.cover)) {
    if (isCoverTextFieldBlank(drawable)) blank.add(drawable.textType as string);
  }

  // Every collected slot is a visible one, so it prints: a blank twin of an already-filled type is still an
  // empty field on the finished cover. Only the reported LABELS collapse per type, never the check itself.
  return COVER_PROJECT_TEXT_TYPES.filter(textType => blank.has(textType));
}

function rewriteCoverTextDrawables(
  drawables: readonly DrawableData[],
  rewrite: (drawable: DrawableTextData) => DrawableData,
  accept: (drawable: DrawableTextData) => boolean,
): readonly DrawableData[] {
  let changed = false;

  const next = drawables.map(drawable => {
    // Same walk as the gate: a hidden slot never reaches the reader, so it is neither filled nor cleared.
    if (drawable.visible === false || drawable.skipRender === true) return drawable;

    if (isDrawableGroup(drawable)) {
      const children = rewriteCoverTextDrawables(drawable.drawableObjects, rewrite, accept);
      if (children === drawable.drawableObjects) return drawable;
      changed = true;
      return { ...drawable, drawableObjects: children };
    }

    if (!isDrawableText(drawable)) return drawable;
    if (!accept(drawable)) return drawable;

    const rewritten = rewrite(drawable);
    if (rewritten === drawable) return drawable;

    changed = true;
    return rewritten;
  });

  return changed ? next : drawables;
}

/** The collage-level half of the walk, shared by every cover-slot rewrite. Same reference back when nothing changed. */
function rewriteCoverCollages<T extends Pick<BookCoverData, 'collages'>>(
  cover: T,
  rewrite: (drawable: DrawableTextData) => DrawableData,
  accept: (drawable: DrawableTextData) => boolean = isProjectTypedSlot,
): T {
  if (!cover?.collages?.length) return cover;

  let changed = false;
  const collages = cover.collages.map(collage => {
    const drawableObjects = rewriteCoverTextDrawables(collage.drawableObjects, rewrite, accept);
    if (drawableObjects === collage.drawableObjects) return collage;
    changed = true;
    return { ...collage, drawableObjects };
  });

  return changed ? { ...cover, collages } : cover;
}

/**
 * Restates every project-owned cover slot as the ALBUM's own answer: the value this album holds, or
 * nothing at all. Applied when a cover template lands (FKS-10460) — a PSD ships those slots with sample
 * copy («11Б класс», «Средняя школа 17»), and sample copy left standing reads as filled to
 * `missingCoverTextTypes`, walking a stranger's cover straight past the approval gate.
 *
 * The `template` pass is deliberately two-way: absent value ⇒ empty slot, which is what re-arms the muted
 * hint and the gate (FKS-10783). The `refresh` pass is what a server-loaded book gets instead — it only
 * corrects a slot already printing a word, so one album's edit reaches its neighbours (FKS-10905) without
 * blanking anything or quietly satisfying the gate.
 * The `sync` pass is what a master→student cover sync gets: a value lands on every slot, while an absent one
 * blanks only a slot still printing the pack's sample — the album's own word stays until the project states one,
 * because the project settings are not yet the only place that word lives.
 * Person slots (owner name) and free text are not project-owned and never touched.
 * Returns the same reference when every slot already reads correctly.
 */
export function restateCoverProjectTexts<T extends Pick<BookCoverData, 'collages'>>(
  cover: T,
  values: Readonly<Record<string, string | undefined>> | null | undefined,
  pass: CoverProjectTextPass = 'template',
): T {
  const resolved = values ?? {};

  return rewriteCoverCollages(cover, drawable => {
    const value = resolved[drawable.textType as string]?.trim() ?? '';
    const current = drawable.text?.text ?? '';
    // A refresh corrects a word already on the cover; it neither blanks it nor silently fills an empty
    // slot, which is what keeps the missing-text hint and the approval gate armed (FKS-10783).
    if (pass === 'refresh' && (!value || !current.trim())) return drawable;
    if (pass === 'sync' && !value && !isPackSampleSlot(drawable)) return drawable;
    if (current === value) return drawable;
    // Blanking a slot that prints its pack sample keeps the sample's runs — the same rule as the pack carry, so a
    // word stated later finds them whichever door blanked the slot.
    if (!value && current === drawable.templateParams?.text) return { ...drawable, text: { ...drawable.text, text: '' } };

    // A slot the pack transfer emptied still holds its sample's runs ({@link carryCoverTexts}): they index the sample,
    // so the project word takes them from there — the sample's own word byte for byte, a covering run onto any word.
    const runsText = current === '' && drawable.templateParams?.text ? drawable.templateParams.text : current;
    return withDrawableText(drawable, value, runsText);
  });
}

/**
 * The values a `sync` pass needs, completed from the album's cover BEFORE the new template landed: where the project
 * states nothing, the album's own word from a slot of that type stands in. The carried word cannot tell this by
 * itself — after the transfer it sits under the NEW template's sample, and a year «2026» equal to that sample would
 * read as the pack's own copy and blank. A previous slot still printing its own template's sample is no word of the
 * album. Two own slots of one type with different words: the first visible one wins, as one project word would.
 */
export function coverSyncTextValues(
  previousCover: Pick<BookCoverData, 'collages'> | null | undefined,
  values: Readonly<Record<string, string | undefined>> | null | undefined,
): Record<string, string | undefined> {
  const resolved: Record<string, string | undefined> = { ...values };

  for (const drawable of coverProjectTextFields(previousCover)) {
    const textType = drawable.textType as string;
    const own = (drawable.text?.text ?? '').trim();
    if (!own || isPackSampleSlot(drawable) || resolved[textType]?.trim()) continue;
    resolved[textType] = own;
  }

  return resolved;
}

/** Назначение — по САМОЙ коробке: переведённую в свободный текст шаблон всё ещё объявляет школой, а слово в ней — альбома. */
function isFreeTextType(textType: string | undefined): boolean {
  return !textType || textType === TextTypeEnum.FREE_TEXT;
}

/** A FREE slot the pack put there (`idFromTemplate`): only such a slot takes part in the album's cover text memory. */
function isTemplateTextSlot(drawable: DrawableTextData): boolean {
  return isFreeTextType(drawable.textType) && !!drawable.idFromTemplate;
}

/** Место слота на странице как его видит читатель: у текста внутри группы координаты отсчитываются от
 * контейнера группы, поэтому цепочка групп складывается по дороге — иначе вложенная строка, физически
 * лежащая ниже, встала бы в порядке чтения выше верхней строки страницы. */
interface PlacedCoverText {
  readonly drawable: DrawableTextData;
  readonly top: number;
  readonly left: number;
}

function collectPlacedCoverTexts(drawables: readonly DrawableData[], top: number, left: number, out: PlacedCoverText[]): void {
  for (const drawable of drawables) {
    if (drawable.visible === false || drawable.skipRender === true) continue;

    const at = {
      top: top + (drawable.naturalAligns?.container?.top ?? 0),
      left: left + (drawable.naturalAligns?.container?.left ?? 0),
    };

    if (isDrawableGroup(drawable)) {
      collectPlacedCoverTexts(drawable.drawableObjects, at.top, at.left, out);
      continue;
    }

    if (isDrawableText(drawable)) out.push({ drawable, ...at });
  }
}

/** Every visible cover text in the order a reader meets it — the ONE order the carry and the memory share. */
function coverTextsInReadingOrder(cover: Pick<BookCoverData, 'collages'> | null | undefined): readonly DrawableTextData[] {
  const pages = (cover?.collages ?? []).map(collage => {
    const found: PlacedCoverText[] = [];
    collectPlacedCoverTexts(collage.drawableObjects, 0, 0, found);
    // Down the page, then across it — the order a reader takes the lines of one cover page in.
    return found.sort((a, b) => a.top - b.top || a.left - b.left).map(placed => placed.drawable);
  });

  // Same page convention as the spread content memory: the LAST collage is the right page — the front cover the
  // reader opens on — and the first is the back. A single collage is one page, so the reversal leaves it alone.
  return pages.reverse().flat();
}

/**
 * The cover's template text slots in the order a reader meets them: the memory queue fills free slots in this order,
 * and a cover without `memoryEntry` marks takes its entries by it — swap a 4-slot pack for a 2-slot one and the two
 * survivors are the two the reader saw first.
 */
export function coverTextSlotsInReadingOrder(
  cover: Pick<BookCoverData, 'collages'> | null | undefined,
): readonly DrawableTextData[] {
  return coverTextsInReadingOrder(cover).filter(isTemplateTextSlot);
}

/** One entry of the album's cover text memory: a word, `''` for a field the photographer erased, `null` for one never filled. */
export type CoverTextMemoryEntry = string | null;

/**
 * The album's memory of its own cover text after this cover: every slot overwrites ITS entry (`memoryEntry`, or the
 * reading-order index on a cover without marks), while the other entries survive: that is what hands all four texts
 * back when a 2-slot pack is worn in between and the 4-slot one returns. A blank slot is two different facts: a text
 * the photographer ERASED (`''` — the entry keeps its place, so the field stays empty on the wider pack) and a
 * template field he NEVER filled (`null` — no place in the queue, the following texts move up past it). Pure.
 */
export function rememberCoverTexts(
  memory: readonly CoverTextMemoryEntry[] | undefined,
  cover: Pick<BookCoverData, 'collages'> | null | undefined,
): readonly CoverTextMemoryEntry[] {
  const next = Array.isArray(memory) ? [...memory] : [];
  const slots = coverTextSlotsInReadingOrder(cover);

  memoryEntriesOfSlots(slots, next.length).forEach((entry, index) => {
    if (entry < 0) return;
    const text = (slots[index].text?.text ?? '').trim();
    next[entry] = text || (typeof next[entry] === 'string' ? '' : null);
  });

  return next;
}

/**
 * Слот без пометки, печатающий СВОЁ слово пака (текст равен тексту шаблона): образец пака текстом альбома не
 * становится и записи памяти не получает — тот же признак «не правили», которым содержимое разворотов
 * отличает заготовку шаблона от текста пользователя.
 */
export function isPackSampleSlot(slot: DrawableTextData): boolean {
  if (slot.memoryEntry !== undefined) return false;
  const sample = slot.templateParams?.text?.trim();
  return !!sample && (slot.text?.text ?? '').trim() === sample;
}

/** Пометка годится, только если адресует запись, которую эта обложка вообще может занять: память плюс её слоты. */
function isMemoryEntryMark(entry: unknown, bound: number): entry is number {
  return typeof entry === 'number' && Number.isInteger(entry) && entry >= 0 && entry < bound;
}

/**
 * Какая запись памяти стоит за каждым слотом обложки (по порядку чтения); `-1` — слот в память не идёт.
 * Номер записи ВЕЗЁТ САМ СЛОТ (`memoryEntry`, ставит перенос): у узкого пака свободных полей меньше, и
 * исправленное на нём слово должно вернуться в СВОЮ запись, а не в первую свободную. Обложка без
 * единой пометки — та, что жила до памяти, и новая: её записи назначает порядок чтения, один раз. Слот без
 * пометки на помеченной обложке (очередь до него не доехала, либо его показали позже) занимает место за
 * последней помеченной записью И за хвостом памяти: перенос и запоминание называют один номер, потому что
 * оба считают его от одной и той же памяти, — иначе он затирал бы живую запись хвоста.
 */
function memoryEntriesOfSlots(slots: readonly DrawableTextData[], memoryLength = 0): readonly number[] {
  const bound = memoryLength + slots.length;
  const carried = slots.map(slot => (isMemoryEntryMark(slot.memoryEntry, bound) ? slot.memoryEntry : -1));
  const sample = slots.map(isPackSampleSlot);
  if (carried.every(entry => entry < 0)) return slots.map((_, index) => (sample[index] ? -1 : index));

  let free = Math.max(Math.max(...carried) + 1, memoryLength);
  return carried.map((entry, index) => (entry >= 0 ? entry : sample[index] ? -1 : free++));
}

/** With a memory in hand the rewrite also reaches free template slots — the queue fills them in reading order. */
function memoryFillableSlot(drawable: DrawableTextData): boolean {
  return isProjectTypedSlot(drawable) || isTemplateTextSlot(drawable);
}

/**
 * Очередь переноса: запомненные тексты — в порядке памяти — занимают СВОБОДНЫЕ поля новой обложки по
 * порядку чтения. Слоты своего назначения в очередь не входят вовсе: их слово держит проект, и оно
 * приезжает на обложку своим законом (`restateCoverProjectTexts`), а не памятью альбома.
 *
 * `entries` — номер записи, доставшийся каждому свободному полю: он уезжает на слот, чтобы правка
 * вернулась в свою запись, а не в первую свободную.
 */
function withRememberedCoverTexts(
  cover: Pick<BookCoverData, 'collages'>,
  memory: readonly CoverTextMemoryEntry[],
): { values: ReadonlyMap<string, string>; entries: ReadonlyMap<string, number> } {
  const entries = new Map<string, number>();

  // Стёртая запись (`''`) тоже занимает своё поле: стёртый на узкой обложке текст не сдвигает соседей на широкой.
  // Никогда не заполненное поле шаблона (`null`) места в очереди не имеет — следующие тексты едут через него,
  // как и запись не-строкой: на обложку уезжает только текст.
  const queue = memory.flatMap((text, index) => (typeof text !== 'string' ? [] : [{ text: text.trim(), entry: index }]));

  const values = new Map<string, string>();
  for (const slot of coverTextSlotsInReadingOrder(cover)) {
    const remembered = queue.shift();
    if (remembered === undefined) break;
    values.set(slot.id, remembered.text);
    entries.set(slot.id, remembered.entry);
  }

  return { values, entries };
}

/**
 * Текст слотов обложки, на которую только что лёг шаблон. Слот СВОЕГО назначения (школа/класс/город/год)
 * назначение сохраняет и ПУСТЕЕТ: образец пака — рисунок пака, а не факт альбома, и оставленный стоять он
 * прошёл бы гейт утверждения чужой школой; слово прежней обложки сюда не едет — слот читает только настройки
 * проекта ({@link restateCoverProjectTexts}), и пустая настройка даёт пустой слот, то есть взведённый гейт.
 * `memory` — собственные тексты альбома по порядку чтения ({@link rememberCoverTexts}): они занимают
 * СВОБОДНЫЕ поля нового пака; без памяти свободные поля печатают своё слово пака.
 * Возвращает ту же ссылку, когда менять нечего.
 */
export function carryCoverTexts<T extends Pick<BookCoverData, 'collages'>>(
  cover: T,
  memory?: readonly CoverTextMemoryEntry[],
): T {
  // Память ведёт перенос только когда она и правда очередь записей: сохранённое чем-то другим поле
  // (строка, число) не разбирается по буквам, а просто не ведёт ничего.
  const remembered = Array.isArray(memory) && memory.length > 0 ? memory : null;
  const { values, entries } = remembered
    ? withRememberedCoverTexts(cover, remembered)
    : { values: new Map<string, string>(), entries: new Map<string, number>() };

  return rewriteCoverCollages(
    cover,
    drawable => {
      if (isProjectTypedSlot(drawable)) {
        // Назначение слота остаётся при нём: по нему его находят и подстановка настроек проекта, и гейт.
        const current = drawable.text?.text ?? '';
        if (current === '') return drawable;
        // Слот, печатающий образец пака, пустеет, но раны образца держит: они индексируют образец
        // (`templateParams.text`), и подстановка слова проекта ({@link restateCoverProjectTexts}) берёт их оттуда.
        if (current === drawable.templateParams?.text) return { ...drawable, text: { ...drawable.text, text: '' } };
        return withDrawableText(drawable, '');
      }

      const text = values.get(drawable.id);
      // Свободное поле пака, до которого очередь не доехала, печатает СВОЁ слово: у него нет назначения,
      // которое обязано говорить про этот альбом, и пустая строка на месте рисунка пака — потеря дизайна.
      if (text === undefined) return drawable;

      const entry = entries.get(drawable.id);
      // A free slot that already reads that way has nothing to be told — keeps the same-reference contract.
      if ((drawable.text?.text ?? '') === text && drawable.memoryEntry === entry) return drawable;

      return { ...withDrawableText(drawable, text), memoryEntry: entry };
    },
    remembered ? memoryFillableSlot : undefined,
  );
}

// ─── v1-твины текстовых слотов обложки (custom-template-v1, дерево CustomTemplateBox) ────────────
// Закон один на обе формы и живёт выше: что считать проектным слотом (`isProjectTextType`) и что с ним
// делает перенос (пустеет, слово проекта между паками не едет — закон A). Расходится ровно две
// вещи — адрес поля (`params.textType`/`params.text` против плоского `textType`/`text.text`) и форма
// узла обхода (`boxes` группы против `drawableObjects`). Глубина обхода и гейт видимости общие:
// скрытый слот до читателя не доходит, значит его не заполняют и не чистят, а скрытая группа
// прячет всё поддерево. Две ветки — состояние перехода: v0-ветвь уйдёт с последним вызывающим
// drawable-формы.
//
// Прогоны обе формы гонят через `carryStyleRunsForTextChange`: боксовая держит их в `params.styleRuns`,
// drawable-форма — в сумке `templateMeta.styleRuns` (`withDrawableText`).

function collectVisibleCoverTextBoxes(boxes: readonly CustomTemplateBox[], out: CustomTextTemplateBox[]): void {
  for (const box of boxes) {
    if (box.visible === false || box.skipRender === true) continue;

    if (box.type === BoxType.GROUP) {
      collectVisibleCoverTextBoxes(box.boxes, out);
      continue;
    }

    if (box.type === BoxType.TEXT) out.push(box);
  }
}

function visibleCoverTextBoxes(cover: Pick<PersistedSpreadV1, 'collages'> | null | undefined): CustomTextTemplateBox[] {
  const found: CustomTextTemplateBox[] = [];
  for (const collage of cover?.collages ?? []) collectVisibleCoverTextBoxes(collage.boxes ?? [], found);
  return found;
}

/** v1-твин {@link coverProjectTextFields}. */
export function coverProjectTextFieldsBoxes(
  cover: Pick<PersistedSpreadV1, 'collages'> | null | undefined,
): readonly CustomTextTemplateBox[] {
  return visibleCoverTextBoxes(cover).filter(box => isProjectTextType(box.params.textType));
}

function rewriteCoverTextBoxes(
  boxes: readonly CustomTemplateBox[],
  rewrite: (box: CustomTextTemplateBox) => CustomTemplateBox,
): readonly CustomTemplateBox[] {
  let changed = false;

  const next = boxes.map(box => {
    if (box.visible === false || box.skipRender === true) return box;

    if (box.type === BoxType.GROUP) {
      const children = rewriteCoverTextBoxes(box.boxes, rewrite);
      if (children === box.boxes) return box;
      changed = true;
      return { ...box, boxes: children as CustomTemplateBox[] };
    }

    if (box.type !== BoxType.TEXT) return box;
    if (!isProjectTextType(box.params.textType)) return box;

    const rewritten = rewrite(box);
    if (rewritten === box) return box;

    changed = true;
    return rewritten;
  });

  return changed ? next : boxes;
}

/** Тот же обход на уровне коллажей: та же ссылка обратно, когда менять нечего. */
function rewriteCoverCollagesBoxes<T extends Pick<PersistedSpreadV1, 'collages'>>(
  cover: T,
  rewrite: (box: CustomTextTemplateBox) => CustomTemplateBox,
): T {
  if (!cover?.collages?.length) return cover;

  let changed = false;
  const collages = cover.collages.map(collage => {
    const boxes = rewriteCoverTextBoxes(collage.boxes ?? [], rewrite);
    if (boxes === collage.boxes) return collage;
    changed = true;
    return { ...collage, boxes } satisfies PersistedCollageV1;
  });

  return changed ? { ...cover, collages } : cover;
}

/** v1-твин {@link carryCoverTexts}. Памяти альбома у боксовой ветви нет: её зовёт только сборка. */
export function carryCoverTextsBoxes<T extends Pick<PersistedSpreadV1, 'collages'>>(cover: T): T {
  return rewriteCoverCollagesBoxes(cover, box => {
    if (!box.params.text) return box;
    // Тот же закон, что у v0-ветви: слот с образцом пака пустеет, но раны образца держит — они индексируют
    // `templateText`, и слово проекта берёт их оттуда (кодек везёт `templateText` в `templateParams.text`).
    if (box.params.text === box.params.templateText) return { ...box, params: { ...box.params, text: '' } };
    // Иначе прогоны адресуют буквы чужого слова и идут общим законом смены текста кодом: пустой текст
    // их сбрасывает. Пустой список = прогонов нет, ключ снимается (конвенция обоих вызывающих закона).
    const runs = carryStyleRunsForTextChange(box.params.styleRuns, box.params.text, '');
    const paragraphRuns = remapParagraphRunsForTextChange(box.params.paragraphRuns, box.params.text, '');

    return {
      ...box,
      params: {
        ...box.params,
        text: '',
        ...(runs === undefined ? {} : { styleRuns: runs.length > 0 ? [...runs] : undefined }),
        ...(paragraphRuns === undefined ? {} : { paragraphRuns: paragraphRuns.length > 0 ? paragraphRuns : undefined }),
      },
    };
  });
}
